import { store } from '../../store';
import { setInstance, clearInstance } from 'store/features/instanceSlice';
import clearReduxStore from 'utils/clear-redux-store';
import * as tokenStore from 'services/auth/tokenStore';
import { resetNativeOAuth } from 'services/auth/nativeOAuth';
import { isNativePlatform } from 'services/auth/platform';
import clearInstanceState from 'services/config/instanceTeardown';
import * as runtimeConfig from 'services/config/runtimeConfig';
import type { RuntimeTenantConfig } from 'services/config/runtimeConfig';

/**
 * Connecting the native app to a customer instance. Given a web host (from a deep link or manual entry),
 * fetch that instance's public `native-config.json`, validate it, and apply it as the runtime tenant config.
 * Shared by InstanceConnect and DeepLinkHandler.
 */

const NATIVE_CONFIG_FETCH_TIMEOUT_MS = 10000;

export class InstanceConnectError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'InstanceConnectError';
  }
}

// Every customer instance lives at `<alias>.<baseDomain>` (e.g. dev.atlasone.co), so the picker takes just the alias.
// Overridable via env for non-default deployments.
const INSTANCE_BASE_DOMAIN = import.meta.env.VITE_APP_INSTANCE_BASE_DOMAIN || 'atlasone.co';

// Local dev hosts are served over cleartext http (the Vite dev server / `cap:dev` `CAP_SERVER_URL`);
// 10.0.2.2 is the Android emulator's alias for the host machine's loopback.
const isLocalHost = (host: string): boolean =>
  host === 'localhost' ||
  host.startsWith('localhost:') ||
  host.startsWith('127.0.0.1') ||
  host.startsWith('10.0.2.2') ||
  /^192\.168\./.test(host) ||
  /^10\./.test(host) ||
  /^172\.(1[6-9]|2\d|3[01])\./.test(host);

/**
 * Resolves picker/deep-link input to a concrete `{ host, baseUrl }`.
 * - A bare alias ("dev", "test") expands to "<alias>.<baseDomain>".
 * - A full host ("dev.atlasone.co", "10.0.2.2:3000") - anything containing a dot or port - is used
 *   verbatim. This covers the deep link's `host=` param and local dev servers.
 * Local hosts use http (the dev server is cleartext); everything else uses https.
 */
export const resolveInstance = (input: string): { host: string; baseUrl: string } => {
  const withoutScheme = (input || '').trim().replace(/^[a-z]+:\/\//i, '');
  const hostPart = withoutScheme.split('/')[0].split('?')[0].split('#')[0].toLowerCase();
  if (!hostPart) return { host: '', baseUrl: '' };

  const host = hostPart.includes('.') || hostPart.includes(':') ? hostPart : `${hostPart}.${INSTANCE_BASE_DOMAIN}`;
  const scheme = isLocalHost(host) ? 'http' : 'https';
  return { host, baseUrl: `${scheme}://${host}` };
};

const isNonEmptyString = (value: unknown): value is string => typeof value === 'string' && value.length > 0;

/**
 * Validates a parsed native-config.json payload.
 * The OAuth ids are only required for the auth methods the instance actually enables,
 * so a password-only instance need not ship Google/Microsoft ids.
 */
const validateConfig = (data: unknown, host: string): RuntimeTenantConfig => {
  if (typeof data !== 'object' || data === null) {
    throw new InstanceConnectError('The instance returned an invalid configuration.');
  }

  const cfg = data as Record<string, unknown>;

  if (cfg.schemaVersion !== runtimeConfig.RUNTIME_CONFIG_SCHEMA_VERSION) {
    throw new InstanceConnectError('This instance is not compatible with this app version. Please update the app.');
  }

  if (!isNonEmptyString(cfg.apiBaseUrl) || !isNonEmptyString(cfg.webAppBaseUrl)) {
    throw new InstanceConnectError('The instance configuration is missing required fields.');
  }

  const authMethods = isNonEmptyString(cfg.authMethods) ? cfg.authMethods : 'microsoft,google,password';
  const methods = authMethods.split(',').map((method) => method.trim().toLowerCase());
  const google = (cfg.google as RuntimeTenantConfig['google']) ?? { webClientId: '', iosClientId: '' };
  const microsoft = (cfg.microsoft as RuntimeTenantConfig['microsoft']) ?? { clientId: '', tenantId: 'common' };

  if (methods.includes('google') && !isNonEmptyString(google.webClientId)) {
    throw new InstanceConnectError('The instance enables Google sign-in but is missing its Google configuration.');
  }
  if (methods.includes('microsoft') && !isNonEmptyString(microsoft.clientId)) {
    throw new InstanceConnectError('The instance enables Microsoft sign-in but is missing its Microsoft configuration.');
  }

  return {
    schemaVersion: runtimeConfig.RUNTIME_CONFIG_SCHEMA_VERSION,
    host,
    apiBaseUrl: cfg.apiBaseUrl,
    webAppBaseUrl: cfg.webAppBaseUrl,
    authMethods,
    google: { webClientId: google.webClientId ?? '', iosClientId: google.iosClientId ?? '' },
    microsoft: { clientId: microsoft.clientId ?? '', tenantId: microsoft.tenantId || 'common' },
    sentryDsn: typeof cfg.sentryDsn === 'string' ? cfg.sentryDsn : undefined,
    stripePublishableKey: typeof cfg.stripePublishableKey === 'string' ? cfg.stripePublishableKey : undefined
  };
};

const fetchNativeConfig = async (host: string, baseUrl: string): Promise<unknown> => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), NATIVE_CONFIG_FETCH_TIMEOUT_MS);

  try {
    const response = await fetch(`${baseUrl}/native-config.json`, {
      method: 'GET',
      signal: controller.signal,
      cache: 'no-store',
      headers: { 'X-A1-Native': '1' }
    });

    if (!response.ok) {
      throw new InstanceConnectError(`Couldn't find an AtlasOne instance at ${host}. Check the name and try again.`);
    }

    return await response.json();
  } catch (error) {
    if (error instanceof InstanceConnectError) throw error;
    throw new InstanceConnectError(`Couldn't connect to ${host}. Check the name and your network, then try again.`);
  } finally {
    clearTimeout(timeout);
  }
};

/**
 * Reloads the native WebView so the app cold-boots against the now-current instance.
 */
const reloadNativeShell = (): void => {
  if (isNativePlatform()) {
    window.location.reload();
  }
};

/**
 * Connects to the instance named by `input` (an alias like "demo", or a full host / local dev address).
 * Validates and persists its config, and when the instance differs from the currently configured one,
 * clears the previous instance's tokens and redux state (tokens are instance-specific).
 *
 * @throws InstanceConnectError with a user-facing message on failure.
 */
export const connectInstance = async (input: string): Promise<RuntimeTenantConfig> => {
  const { host, baseUrl } = resolveInstance(input);
  if (!host) {
    throw new InstanceConnectError('Enter your instance name (for example, "demo").');
  }

  const data = await fetchNativeConfig(host, baseUrl);
  const config = validateConfig(data, host);

  const previousHost = runtimeConfig.getConfig()?.host;
  const instanceChanged = previousHost !== host;

  if (instanceChanged) {
    await tokenStore.clear();
    resetNativeOAuth();
    clearReduxStore();
  }

  await runtimeConfig.setConfig(config);
  store.dispatch(setInstance({ host: config.host, webAppBaseUrl: config.webAppBaseUrl }));

  // First connect or a switch changes the active tenant DSN; reload so Sentry re-inits against it.
  if (instanceChanged) {
    await clearInstanceState();
    reloadNativeShell();
  }

  return config;
};

/**
 * Disconnect from the current instance.
 * Clears tokens, runtime config, memoized OAuth init, and redux state.
 */
export const disconnectInstance = async (): Promise<void> => {
  await tokenStore.clear();
  await runtimeConfig.clear();
  resetNativeOAuth();
  clearReduxStore();
  store.dispatch(clearInstance());
  await clearInstanceState();
  reloadNativeShell();
};
